import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { PageHeader, SectionCard, Spinner } from '../components/ui';
import { fmtMoney } from '../lib/format';

// Игровой домен — рефссылка должна вести сюда (там где игроки
// проходят регистрацию), а не на admin.buraclub.online. Если
// когда-нибудь сменим домен — правим только тут.
const GAME_ORIGIN = 'https://buraclub.online';

type Tab = 'overview' | 'players' | 'deposits' | 'retention' | 'commissions' | 'links' | 'payouts';

interface AffiliateStats {
  total_players: string;
  ftd_players: string;
  active_d1: string;
  active_d7: string;
  active_d30: string;
  total_rake: string;
  total_commission: string;
  total_player_share: string;
  today_commission: string;
  today_players: string;
  balance: string;
  hold_balance: string;
  total_earned: string;
  total_withdrawn: string;
}

interface AffiliateLink {
  id: string;
  affiliate_user_id: string;
  code: string;
  commission_pct: string;
  clicks: number;
  registrations: number;
  active: boolean;
  notes: string | null;
  created_at: string;
}

interface AffiliatePlayer {
  id: string;
  nickname: string;
  balance: string;
  is_dealer: boolean;
  online: boolean;
  created_at: string;
  last_seen: string;
  affiliate_assigned_at: string | null;
  first_deposit_at: string | null;
  total_commission: string;
  total_player_share: string;
  total_rake_credit: string;
}

interface AffiliateCommission {
  id: string;
  affiliate_user_id: string;
  rake_log_id: string;
  player_user_id: string;
  player_nickname: string;
  affiliate_link_id: string | null;
  room_id: string;
  player_share: string;
  rake_amount: string;
  commission_pct: string;
  commission_amount: string;
  ts: string;
}

interface AffiliateDeposit {
  id: string;
  nickname: string;
  balance: string;
  is_dealer: boolean;
  online: boolean;
  first_deposit_at: string;
}

interface AffiliateRetentionCohort {
  cohort_date: string;
  cohort_size: string;
  active_d1: string;
  active_d7: string;
  active_d30: string;
  ftd_count: string;
}

interface AffiliatePayout {
  id: string;
  affiliate_user_id: string;
  amount: string;
  crypto_network: string;
  crypto_currency: string;
  crypto_address: string;
  heleket_payout_id: string | null;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled';
  failure_reason: string | null;
  created_at: string;
  processed_at: string | null;
}

function num(v: string | number | undefined | null): number {
  if (v === undefined || v === null) return 0;
  return Number(v);
}

function percent(num: number, denom: number): string {
  if (!denom) return '0%';
  return `${Math.round((num / denom) * 100)}%`;
}

export function AffiliatePage() {
  const [tab, setTab] = useState<Tab>('overview');

  return (
    <div>
      <PageHeader title="Аффилиат" subtitle="Партнёрская программа: игроки, депозиты, ретеншн, комиссии, выплаты" />

      <div className="flex flex-wrap gap-1 mb-4 bg-black/30 rounded-xl p-1 border border-white/10">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>Обзор</TabButton>
        <TabButton active={tab === 'players'} onClick={() => setTab('players')}>Игроки</TabButton>
        <TabButton active={tab === 'deposits'} onClick={() => setTab('deposits')}>Депозиты (FTD)</TabButton>
        <TabButton active={tab === 'retention'} onClick={() => setTab('retention')}>Ретеншн</TabButton>
        <TabButton active={tab === 'commissions'} onClick={() => setTab('commissions')}>Комиссии</TabButton>
        <TabButton active={tab === 'links'} onClick={() => setTab('links')}>Ссылки</TabButton>
        <TabButton active={tab === 'payouts'} onClick={() => setTab('payouts')}>Выплаты</TabButton>
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'players' && <PlayersTab />}
      {tab === 'deposits' && <DepositsTab />}
      {tab === 'retention' && <RetentionTab />}
      {tab === 'commissions' && <CommissionsTab />}
      {tab === 'links' && <LinksTab />}
      {tab === 'payouts' && <PayoutsTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        'flex-1 min-w-[100px] px-3 py-2 text-sm rounded-lg transition ' +
        (active ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-stone-300 hover:bg-white/5')
      }
    >
      {children}
    </button>
  );
}

// ============================ ОБЗОР ============================

function OverviewTab() {
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<AffiliateStats>('/api/crm/affiliate/me/stats')
      .then(setStats)
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;
  }

  const totalPlayers = num(stats.total_players);
  const ftd = num(stats.ftd_players);
  const d1 = num(stats.active_d1);
  const d7 = num(stats.active_d7);
  const d30 = num(stats.active_d30);
  const balance = num(stats.balance);
  const hold = num(stats.hold_balance);
  const avail = balance - hold;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Игроков привлечено" value={String(totalPlayers)} />
        <Kpi label="FTD (с депозитом)" value={String(ftd)} hint={percent(ftd, totalPlayers)} />
        <Kpi label="Активных сегодня (D1)" value={String(d1)} hint={percent(d1, totalPlayers)} />
        <Kpi label="Активных за 7 дней" value={String(d7)} hint={percent(d7, totalPlayers)} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Рейк от ваших игроков" value={fmtMoney(num(stats.total_rake))} />
        <Kpi label="Доля игроков в банках" value={fmtMoney(num(stats.total_player_share))} />
        <Kpi label="Заработано всего" value={fmtMoney(num(stats.total_earned))} accent />
        <Kpi label="Выплачено" value={fmtMoney(num(stats.total_withdrawn))} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Баланс">
          <div className="space-y-2 text-sm">
            <Row label="Доступно" value={<span className="text-emerald-300 font-semibold">{fmtMoney(avail)}</span>} />
            <Row label="В холде" value={<span className="text-amber-300">{fmtMoney(hold)}</span>} />
            <Row label="Всего заработано" value={fmtMoney(num(stats.total_earned))} />
            <Row label="Уже выплачено" value={fmtMoney(num(stats.total_withdrawn))} />
          </div>
        </SectionCard>

        <SectionCard title="Сегодня">
          <div className="space-y-2 text-sm">
            <Row label="Комиссия за 24ч" value={<span className="text-emerald-300">{fmtMoney(num(stats.today_commission))}</span>} />
            <Row label="Активных игроков" value={String(num(stats.today_players))} />
            <Row label="Ретеншн D30" value={percent(d30, totalPlayers)} />
          </div>
        </SectionCard>

        <SectionCard title="Партнёрский дашборд">
          <div className="space-y-1.5 text-xs text-stone-300 leading-relaxed">
            <p>• <b>FTD</b> — игроки, которые сделали первый депозит (баланс &gt; 0).</p>
            <p>• <b>Ретеншн</b> — % от всех привлечённых, активных за D1/D7/D30.</p>
            <p>• <b>Комиссия</b> начисляется с rake комнат, в которых играли ваши игроки. Никаких двойных начислений.</p>
            <p>• <b>Выплаты</b> через Heleket. Создайте заявку — деньги холдируются до подтверждения админом.</p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className={(accent ? 'value text-emerald-300' : 'value')}>{value}</div>
      {hint ? <div className="text-[11px] text-stone-500 mt-1">{hint}</div> : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-stone-400">{label}</span>
      <span className="text-stone-100 font-mono text-sm">{value}</span>
    </div>
  );
}

// ============================ ИГРОКИ ============================

function PlayersTab() {
  const [items, setItems] = useState<AffiliatePlayer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ftdOnly, setFtdOnly] = useState(false);
  const [activeDays, setActiveDays] = useState<string>('');

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (search.trim()) qs.set('search', search.trim());
    if (ftdOnly) qs.set('ftdOnly', '1');
    if (activeDays) qs.set('activeWithinDays', activeDays);
    qs.set('limit', '100');
    api
      .get<{ items: AffiliatePlayer[]; total: number }>(`/api/crm/affiliate/me/players?${qs.toString()}`)
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load();
  };

  return (
    <div className="space-y-3">
      <form onSubmit={onSearch} className="flex flex-wrap gap-2 items-center">
        <input
          className="input-modern flex-1 min-w-[200px]"
          placeholder="Поиск по нику"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-modern w-auto" value={activeDays} onChange={(e) => setActiveDays(e.target.value)}>
          <option value="">Активность: любая</option>
          <option value="1">Активен за 1 день</option>
          <option value="7">Активен за 7 дней</option>
          <option value="30">Активен за 30 дней</option>
        </select>
        <label className="flex items-center gap-1 text-sm text-stone-300">
          <input type="checkbox" checked={ftdOnly} onChange={(e) => setFtdOnly(e.target.checked)} />
          Только с депозитом (FTD)
        </label>
        <button type="submit" className="btn-gold px-3 py-1.5 text-sm">Применить</button>
        <div className="text-xs text-stone-500 ml-auto">Найдено: {total}</div>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>
      ) : items.length === 0 ? (
        <SectionCard>
          <div className="text-sm text-stone-500 text-center py-6">
            Пока нет привлечённых игроков. Скопируйте свою рефссылку и отправьте её игрокам.
          </div>
        </SectionCard>
      ) : (
        <div className="glass-strong rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-stone-400 bg-black/30">
                <tr>
                  <th className="px-3 py-2">Ник</th>
                  <th className="px-3 py-2">Баланс</th>
                  <th className="px-3 py-2">FTD</th>
                  <th className="px-3 py-2">Активность</th>
                  <th className="px-3 py-2">Регистрация (aff)</th>
                  <th className="px-3 py-2">Рейк от игрока</th>
                  <th className="px-3 py-2">Комиссия</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-3 py-2 font-medium text-stone-100">
                      <span className="inline-flex items-center gap-1">
                        {p.online ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" /> : <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-600" />}
                        {p.nickname}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono">{fmtMoney(num(p.balance))}</td>
                    <td className="px-3 py-2">
                      {p.first_deposit_at
                        ? <span className="text-emerald-300 text-xs">есть</span>
                        : <span className="text-stone-500 text-xs">нет</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-400">
                      {fmtTs(p.last_seen)}
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-400">
                      {p.affiliate_assigned_at ? fmtTs(p.affiliate_assigned_at) : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono">{fmtMoney(num(p.total_rake_credit))}</td>
                    <td className="px-3 py-2 font-mono text-emerald-300">
                      {fmtMoney(num(p.total_commission))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ ДЕПОЗИТЫ ============================

function DepositsTab() {
  const [items, setItems] = useState<AffiliateDeposit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<{ items: AffiliateDeposit[]; total: number }>('/api/crm/affiliate/me/deposits?limit=200')
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;

  return (
    <SectionCard title="FTD — игроки, сделавшие первый депозит" subtitle={`Всего: ${total}`}>
      {items.length === 0 ? (
        <div className="text-sm text-stone-500 text-center py-4">
          FTD не зафиксировано. Когда привлечённый игрок получает первое пополнение баланса через дилера, здесь появится запись.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-stone-400">
              <tr>
                <th className="px-3 py-2">Ник</th>
                <th className="px-3 py-2">Текущий баланс</th>
                <th className="px-3 py-2">Дата FTD</th>
                <th className="px-3 py-2">Онлайн</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-stone-100">{d.nickname}</td>
                  <td className="px-3 py-2 font-mono">{fmtMoney(num(d.balance))}</td>
                  <td className="px-3 py-2 text-stone-400 text-xs">{fmtTs(d.first_deposit_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {d.online
                      ? <span className="text-emerald-300">в сети</span>
                      : <span className="text-stone-500">не в сети</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ============================ РЕТЕНШН ============================

function RetentionTab() {
  const [items, setItems] = useState<AffiliateRetentionCohort[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<{ items: AffiliateRetentionCohort[] }>('/api/crm/affiliate/me/retention')
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;

  if (items.length === 0) {
    return (
      <SectionCard title="Когорты по дате регистрации">
        <div className="text-sm text-stone-500 text-center py-4">Нет данных. Появятся когда привлечённые игроки начнут возвращаться.</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Ретеншн — когорты по дню регистрации" subtitle="Сколько из привлечённых игроков активны на D1 / D7 / D30">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-stone-400">
            <tr>
              <th className="px-3 py-2">Дата когорты</th>
              <th className="px-3 py-2">Размер</th>
              <th className="px-3 py-2">FTD</th>
              <th className="px-3 py-2">D1</th>
              <th className="px-3 py-2">D7</th>
              <th className="px-3 py-2">D30</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.cohort_date} className="border-t border-white/5">
                <td className="px-3 py-2 font-mono text-stone-300">{c.cohort_date}</td>
                <td className="px-3 py-2 font-mono">{c.cohort_size}</td>
                <td className="px-3 py-2 text-emerald-300 font-mono">{c.ftd_count}</td>
                <td className="px-3 py-2">
                  <Bar ratio={num(c.active_d1) / Math.max(1, num(c.cohort_size))} value={c.active_d1} total={c.cohort_size} />
                </td>
                <td className="px-3 py-2">
                  <Bar ratio={num(c.active_d7) / Math.max(1, num(c.cohort_size))} value={c.active_d7} total={c.cohort_size} />
                </td>
                <td className="px-3 py-2">
                  <Bar ratio={num(c.active_d30) / Math.max(1, num(c.cohort_size))} value={c.active_d30} total={c.cohort_size} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function Bar({ ratio, value, total }: { ratio: number; value: string; total: string }) {
  const pct = Math.round(ratio * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-400" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-stone-300 w-16 text-right">{value}/{total}</span>
    </div>
  );
}

// ============================ КОМИССИИ ============================

function CommissionsTab() {
  const [items, setItems] = useState<AffiliateCommission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<{ items: AffiliateCommission[]; total: number }>('/api/crm/affiliate/me/commissions?limit=200')
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;

  const sum = items.reduce((s, c) => s + num(c.commission_amount), 0);

  return (
    <SectionCard title={`История начислений (${total})`} subtitle={`Последние ${items.length} · сумма: ${fmtMoney(sum)}`}>
      {items.length === 0 ? (
        <div className="text-sm text-stone-500 text-center py-4">Начислений пока нет. Они появятся, когда ваши игроки сыграют партии.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-stone-400">
              <tr>
                <th className="px-3 py-2">Дата</th>
                <th className="px-3 py-2">Игрок</th>
                <th className="px-3 py-2">Комната</th>
                <th className="px-3 py-2">Доля игрока</th>
                <th className="px-3 py-2">Rake комнаты</th>
                <th className="px-3 py-2">%</th>
                <th className="px-3 py-2">Комиссия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-xs text-stone-400">{fmtTs(c.ts)}</td>
                  <td className="px-3 py-2 text-stone-100">{c.player_nickname}</td>
                  <td className="px-3 py-2 text-xs text-stone-500 font-mono">{c.room_id.slice(0, 8)}…</td>
                  <td className="px-3 py-2 font-mono">{fmtMoney(num(c.player_share))}</td>
                  <td className="px-3 py-2 font-mono">{fmtMoney(num(c.rake_amount))}</td>
                  <td className="px-3 py-2 text-xs text-stone-400">{c.commission_pct}%</td>
                  <td className="px-3 py-2 font-mono text-emerald-300">{fmtMoney(num(c.commission_amount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ============================ ССЫЛКИ ============================

function LinksTab() {
  const [items, setItems] = useState<AffiliateLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get<{ items: AffiliateLink[] }>('/api/crm/affiliate/me/links')
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, []);

  const refUrl = (code: string) => `${GAME_ORIGIN}/r/${encodeURIComponent(code)}`;

  const copy = async (text: string, code: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // fallback
      const sel = document.createElement('textarea');
      sel.value = text;
      document.body.appendChild(sel);
      sel.select();
      document.execCommand('copy');
      document.body.removeChild(sel);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;

  if (items.length === 0) {
    return (
      <SectionCard title="Рефссылки">
        <div className="text-sm text-stone-500 text-center py-6">
          Ссылок нет. Обновите страницу через несколько секунд — система автоматически создаст дефолтную рефссылку.
          <br />
          <span className="text-stone-600 text-xs">После создания суперадмин назначит % комиссии в разделе «Аффилиаты · Админ».</span>
        </div>
      </SectionCard>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((l) => {
        const url = refUrl(l.code);
        const cr = l.clicks ? Math.round((l.registrations / l.clicks) * 100) : 0;
        return (
          <SectionCard key={l.id} title={l.code} subtitle={`Комиссия: ${l.commission_pct}% · ${l.active ? 'активна' : 'выключена'}`}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  className="input-modern flex-1 font-mono text-xs"
                  value={url}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button className="btn-gold px-3 py-1.5 text-sm" onClick={() => copy(url, l.code)}>
                  {copied === l.code ? 'Скопировано ✓' : 'Копировать'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Кликов" value={String(l.clicks)} />
                <Kpi label="Регистраций" value={String(l.registrations)} />
                <Kpi label="CR" value={`${cr}%`} hint="регистраций / кликов" />
              </div>
              {l.notes ? <div className="text-xs text-stone-400 italic">Заметка: {l.notes}</div> : null}
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
}

// ============================ ВЫПЛАТЫ ============================

function PayoutsTab() {
  const [items, setItems] = useState<AffiliatePayout[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<AffiliateStats | null>(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [p, s] = await Promise.all([
        api.get<{ items: AffiliatePayout[]; total: number }>('/api/crm/affiliate/me/payouts?limit=100'),
        api.get<AffiliateStats>('/api/crm/affiliate/me/stats')
      ]);
      setItems(p.items);
      setTotal(p.total);
      setStats(s);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const avail = stats ? num(stats.balance) - num(stats.hold_balance) : 0;

  return (
    <div className="space-y-4">
      <SectionCard title="Новая заявка на выплату">
        <NewPayoutForm avail={avail} onCreated={load} />
      </SectionCard>

      <SectionCard title={`История заявок (${total})`}>
        {loading ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">Заявок ещё не было.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-stone-400">
                <tr>
                  <th className="px-3 py-2">Дата</th>
                  <th className="px-3 py-2">Сумма</th>
                  <th className="px-3 py-2">Сеть</th>
                  <th className="px-3 py-2">Кошелёк</th>
                  <th className="px-3 py-2">Статус</th>
                  <th className="px-3 py-2">Heleket ID</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="px-3 py-2 text-xs text-stone-400">{fmtTs(p.created_at)}</td>
                    <td className="px-3 py-2 font-mono">{fmtMoney(num(p.amount))}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.crypto_network} <span className="text-stone-500">({p.crypto_currency})</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-stone-300 max-w-[180px] truncate" title={p.crypto_address}>
                      {p.crypto_address}
                    </td>
                    <td className="px-3 py-2">
                      <PayoutStatusBadge status={p.status} reason={p.failure_reason} />
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-500 font-mono">
                      {p.heleket_payout_id ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {p.status === 'pending' ? (
                        <button
                          className="text-xs px-2 py-1 rounded border border-rose-400/30 text-rose-300 hover:bg-rose-500/10"
                          onClick={async () => {
                            if (!window.confirm('Отменить заявку? Холд с баланса будет снят.')) return;
                            try {
                              await api.post(`/api/crm/affiliate/me/payouts/${p.id}/cancel`);
                              await load();
                            } catch (e: any) {
                              window.alert('Ошибка: ' + (e?.message ?? 'unknown'));
                            }
                          }}
                        >
                          Отменить
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-xs text-stone-500 mt-3">
          {refreshing ? 'обновление…' : 'автообновление не активно — создайте новую заявку для перезапроса'}
        </div>
      </SectionCard>
    </div>
  );
}

function PayoutStatusBadge({ status, reason }: { status: AffiliatePayout['status']; reason: string | null }) {
  const colors: Record<AffiliatePayout['status'], string> = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    processing: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
    paid: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    failed: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    cancelled: 'bg-stone-500/20 text-stone-300 border-stone-500/30'
  };
  const labels: Record<AffiliatePayout['status'], string> = {
    pending: 'Ожидает',
    processing: 'В обработке',
    paid: 'Выплачено',
    failed: 'Ошибка',
    cancelled: 'Отменена'
  };
  return (
    <span title={reason ?? ''} className={'inline-block px-2 py-0.5 text-xs rounded border ' + colors[status]}>
      {labels[status]}
    </span>
  );
}

function NewPayoutForm({ avail, onCreated }: { avail: number; onCreated: () => Promise<void> | void }) {
  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('TRC20');
  const [currency, setCurrency] = useState('USDT');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const a = Math.floor(Number(amount));
    if (!Number.isFinite(a) || a <= 0) {
      setError('Сумма должна быть положительным числом.');
      return;
    }
    if (a > avail) {
      setError(`Сумма превышает доступный баланс (${fmtMoney(avail)}).`);
      return;
    }
    if (!address || address.length < 10) {
      setError('Укажите корректный адрес кошелька.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/crm/affiliate/me/payouts', {
        amount: a,
        cryptoNetwork: network,
        cryptoCurrency: currency,
        cryptoAddress: address
      });
      setAmount('');
      setAddress('');
      await onCreated();
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось создать заявку.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Сумма (к выплате)">
          <input
            className="input-modern"
            placeholder="например 1000"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </Field>
        <Field label="Сеть">
          <select className="input-modern" value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="TRC20">TRC20 (Tron)</option>
            <option value="ERC20">ERC20 (Ethereum)</option>
            <option value="BEP20">BEP20 (BSC)</option>
            <option value="BTC">BTC</option>
            <option value="LTC">LTC</option>
            <option value="other">Другая</option>
          </select>
        </Field>
        <Field label="Валюта">
          <select className="input-modern" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="USDT">USDT</option>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
            <option value="LTC">LTC</option>
            <option value="TRX">TRX</option>
          </select>
        </Field>
        <Field label="Доступно">
          <div className="input-modern font-mono text-emerald-300">{fmtMoney(avail)}</div>
        </Field>
      </div>
      <Field label="Адрес кошелька">
        <input
          className="input-modern font-mono"
          placeholder="адрес для получения"
          value={address}
          onChange={(e) => setAddress(e.target.value.trim())}
        />
      </Field>
      {error ? <div className="text-sm text-rose-300">{error}</div> : null}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={submitting} className="btn-gold px-4 py-2 text-sm disabled:opacity-50">
          {submitting ? 'Создание…' : 'Создать заявку'}
        </button>
        <span className="text-xs text-stone-500">
          При создании заявки сумма холдируется и не может быть использована повторно до отмены или выплаты.
        </span>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-stone-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

function fmtTs(ts: string | number | null | undefined): string {
  if (!ts) return '—';
  const n = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(n)) return '—';
  return new Date(n).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}
